/**
 * Contatos do WhatsApp que ainda não são clientes.
 *
 * A tese: a foto de perfil é **discriminador**, não retrato. O barbeiro tem três
 * "João Pedro" na agenda e sabe qual é qual pela imagem — e ela não precisa ser
 * o rosto da pessoa, um carro serve, desde que seja o carro de um João Pedro só.
 * A foto boa (o rosto) chega depois, na conclusão do primeiro atendimento; até
 * lá, é isto que resolve o ovo e a galinha do cadastro.
 *
 * As três regras daqui existem para conter o mesmo risco por caminhos
 * diferentes: **quanto menos consulta ao WhatsApp, melhor**. Varredura de
 * contatos é o que tem cara de raspagem e é o que derruba número; então nada
 * aqui olha a agenda inteira. O rascunho nasce de mensagem recebida, ganha foto
 * só se for procurado, e a foto fica em cache porque buscar de novo a cada
 * busca multiplicaria exatamente o volume que se quer manter baixo.
 */

import { sortByName } from './name.js';

export type ContactOrigin = 'CLIENT' | 'DRAFT';

/** O mínimo que uma ficha precisa expor para disputar um lugar na busca. */
export interface ClientMatch {
  id: string;
  name: string;
  /** E.164. */
  phone: string;
}

export interface DraftMatch {
  id: string;
  name: string;
  /** E.164. */
  phone: string;
  /** Nulo enquanto o rascunho não virou ficha. */
  promotedAt: Date | null;
}

export interface ContactCandidate<C extends ClientMatch, D extends DraftMatch> {
  origin: ContactOrigin;
  client: C | null;
  draft: D | null;
  name: string;
  phone: string;
}

/**
 * A lista que o barbeiro vê ao buscar alguém para agendar: fichas e rascunhos
 * numa ordem só.
 *
 * Duas listas separadas na tela parecem mais honestas e são piores de usar: ele
 * procura "João", e o João que interessa pode estar em qualquer das duas — daí
 * teria de ler as duas até o fim. Uma lista em ordem de nome é como ele já acha
 * gente hoje.
 *
 * O rascunho é descartado quando **já existe ficha com aquele telefone**. Sem
 * isso, todo cliente antigo que mandar mensagem apareceria duas vezes na busca,
 * e o barbeiro escolheria no chute qual dos dois é o de verdade. O telefone é o
 * critério porque é a chave de identidade do cliente em todo o resto do sistema
 * — nome não serve justamente por causa dos três João Pedro.
 */
export function mergeContactCandidates<C extends ClientMatch, D extends DraftMatch>(
  clients: readonly C[],
  drafts: readonly D[],
): ContactCandidate<C, D>[] {
  const takenPhones = new Set(clients.map((client) => client.phone));

  const candidates: ContactCandidate<C, D>[] = clients.map((client) => ({
    origin: 'CLIENT',
    client,
    draft: null,
    name: client.name,
    phone: client.phone,
  }));

  for (const draft of drafts) {
    // Rascunho promovido não volta à busca: a ficha dele já está na lista, e
    // ressurgir como "contato novo" faria o barbeiro cadastrar o mesmo cliente
    // de novo.
    if (draft.promotedAt !== null) continue;
    if (takenPhones.has(draft.phone)) continue;

    candidates.push({
      origin: 'DRAFT',
      client: null,
      draft,
      name: draft.name,
      phone: draft.phone,
    });
  }

  return sortByName(candidates, (candidate) => candidate.name);
}

export interface PhotoCacheState {
  /** Quando o rascunho apareceu num resultado de busca do barbeiro. */
  lastSearchedAt: Date | null;
  /** Quando a ponte foi incumbida da foto. */
  photoRequestedAt: Date | null;
  /** Quando a foto entrou no cache. */
  photoFetchedAt: Date | null;
}

/**
 * Vale buscar a foto deste contato?
 *
 * Só de quem foi **procurado**. É o que impede o retrato do gás, do fornecedor e
 * do grupo da família de existir no banco: eles chegam como rascunho, ficam com
 * nome e telefone, e nunca custam uma consulta de imagem porque ninguém os
 * procura. Buscar a foto de todo mundo que manda mensagem seria mais simples de
 * escrever e transformaria o recurso numa coleta de fotos de terceiros.
 *
 * O efeito colateral é honesto e vale registrar: na **primeira** busca os três
 * João Pedro aparecem sem foto, porque a ponte só descobre o pedido no `outbox`
 * seguinte. A foto entra segundos depois e, dali em diante, é instantânea.
 *
 * O reparo por idade existe porque foto de perfil muda — mas devagar. Buscar de
 * novo a cada busca desfaria o cache e multiplicaria as consultas.
 */
export function needsPhotoFetch(
  state: PhotoCacheState,
  now: Date,
  maxAgeDays: number,
  retryMinutes: number,
): boolean {
  if (state.lastSearchedAt === null) return false;

  // Já pedimos e nada voltou: ou o contato não tem foto de perfil, ou não deixa
  // ver. Perguntar de novo a cada volta do `outbox` seria uma consulta ao
  // servidor do WhatsApp a cada cinco segundos, para sempre, por contato — que
  // é o padrão de enumeração que este recurso inteiro existe para não fazer.
  // Um único vizinho sem foto daria dezessete mil consultas por dia.
  if (
    state.photoRequestedAt !== null &&
    now.getTime() - state.photoRequestedAt.getTime() < retryMinutes * 60 * 1000
  ) {
    return false;
  }

  if (state.photoFetchedAt === null) return true;

  const ageMs = now.getTime() - state.photoFetchedAt.getTime();
  return ageMs >= maxAgeDays * 24 * 60 * 60 * 1000;
}

/**
 * Sabemos que este contato **não tem** foto de perfil?
 *
 * É a única forma de a API responder isso. O contrato com a ponte diz que
 * contato sem foto simplesmente não recebe chamada — a ausência é registrada
 * pela não-chamada —, então "não tem foto" e "a foto ainda não chegou" seriam
 * indistinguíveis daqui sem a marca do pedido. Com ela, a conta é direta:
 * pedimos, a janela passou, nada voltou.
 *
 * Quem já tem foto no cache nunca é "sem foto", mesmo que ela esteja velha e o
 * pedido de renovação não tenha voltado: a imagem que o barbeiro vê continua
 * existindo, e é ela que distingue os três João Pedro.
 *
 * A janela é a mesma dos dois lados — a ponte também espera antes de tentar de
 * novo. Dois números diferentes aqui fariam um dos lados concluir sozinho.
 */
export function isPhotolessDraft(state: PhotoCacheState, now: Date, retryMinutes: number): boolean {
  if (state.photoRequestedAt === null) return false;
  if (state.photoFetchedAt !== null) return false;

  return now.getTime() - state.photoRequestedAt.getTime() >= retryMinutes * 60 * 1000;
}

/**
 * Vale a tela esperar a foto deste contato?
 *
 * A folha de agendar pergunta de novo enquanto houver foto a caminho, e a
 * pergunta precisa ter fim. Uma versão anterior esperava enquanto houvesse
 * rascunho com `photoUrl` nulo — e contato sem foto de perfil tem `photoUrl`
 * nulo para sempre, então bastava um vizinho sem foto na busca para a folha
 * recarregar a cada três segundos enquanto estivesse aberta.
 *
 * "A caminho" é estreito de propósito: a ponte está conectada e ainda não foi
 * incumbida (vai ser no próximo `outbox`), ou foi incumbida há menos de
 * `waitSeconds`. Passado isso, ou o contato não tem foto, ou ela vem na próxima
 * busca — e nenhum dos dois casos justifica a tela continuar perguntando.
 *
 * Foto já no cache nunca está "a caminho", nem a renovação dela: o barbeiro já
 * tem a imagem que distingue os três João Pedro.
 */
export function isPhotoOnTheWay(
  state: Pick<PhotoCacheState, 'photoRequestedAt' | 'photoFetchedAt'>,
  now: Date,
  bridgeConnected: boolean,
  waitSeconds: number,
): boolean {
  if (state.photoFetchedAt !== null) return false;
  if (!bridgeConnected) return false;
  if (state.photoRequestedAt === null) return true;

  return now.getTime() - state.photoRequestedAt.getTime() < waitSeconds * 1000;
}

export interface DraftRetentionState {
  lastMessageAt: Date;
  promotedAt: Date | null;
}

/**
 * Rascunho velho que nunca virou nada pode ser purgado.
 *
 * É a resposta à parte incômoda do recurso: entram no banco pessoas que nunca
 * tiveram nada com a barbearia. A minimização aqui é por **tempo** — quem
 * mandou uma mensagem uma vez, há meses, e nunca foi procurado, não é contato
 * de trabalho de ninguém.
 *
 * Quem já virou ficha nunca é purgado: aquilo deixou de ser rascunho e passou a
 * ser histórico do negócio.
 */
export function isDraftExpired(
  state: DraftRetentionState,
  now: Date,
  retentionDays: number,
): boolean {
  if (state.promotedAt !== null) return false;

  const ageMs = now.getTime() - state.lastMessageAt.getTime();
  return ageMs >= retentionDays * 24 * 60 * 60 * 1000;
}

/**
 * A ponte deu sinal de vida recente?
 *
 * Sem esta conta a queda é silenciosa — e falha silenciosa é o pior defeito
 * possível neste produto: o barbeiro procura um contato, não acha, e conclui que
 * o recurso não presta. Com ela a tela pode dizer "sem sinal desde 14:20", que
 * é uma informação com a qual se faz alguma coisa.
 */
export function isBridgeSignalStale(
  lastHeartbeatAt: Date | null,
  now: Date,
  staleMinutes: number,
): boolean {
  if (lastHeartbeatAt === null) return true;
  return now.getTime() - lastHeartbeatAt.getTime() >= staleMinutes * 60 * 1000;
}

/**
 * O código de pareamento ainda vale?
 *
 * O `PairPhone` do whatsmeow dá 160 segundos. Passado o prazo, o código na tela
 * é mentira: o barbeiro digita, o aparelho recusa, e não há nada na tela que
 * explique. Mostrar código morto é pior que não mostrar nenhum.
 */
export function isPairingCodeValid(expiresAt: Date | null, now: Date): boolean {
  if (expiresAt === null) return false;
  return expiresAt.getTime() > now.getTime();
}
